import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('autogutachter24_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Authentication API
export const authAPI = {
  login: async (email, passwort) => {
    const response = await api.post('/auth/login', { email, passwort });
    return response.data;
  },

  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  registerPublic: async (userData) => {
    const response = await api.post('/auth/self-register', userData);
    return response.data;
  },

  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  getMandant: async () => {
    const response = await api.get('/auth/me/mandant');
    return response.data;
  }
};

// Mandanten API
export const mandantenAPI = {
  create: async (data) => {
    const response = await api.post('/mandanten/', data);
    return response.data;
  },

  getAll: async () => {
    const response = await api.get('/mandanten/');
    return response.data;
  },

  getById: async (mandant_id) => {
    const response = await api.get(`/mandanten/${mandant_id}`);
    return response.data;
  },

  update: async (mandant_id, data) => {
    const response = await api.patch(`/mandanten/${mandant_id}`, data);
    return response.data;
  },

  delete: async (mandant_id) => {
    const response = await api.delete(`/mandanten/${mandant_id}`);
    return response.data;
  }
};

// Fälle API
export const faelleAPI = {
  create: async (data) => {
    const response = await api.post('/faelle/', data);
    return response.data;
  },

  getAll: async (params = {}) => {
    const response = await api.get('/faelle/', { params });
    return response.data;
  },

  getById: async (fall_id) => {
    const response = await api.get(`/faelle/${fall_id}`);
    return response.data;
  },

  update: async (fall_id, data) => {
    const response = await api.patch(`/faelle/${fall_id}`, data);
    return response.data;
  },

  // Dateien
  uploadDatei: async (fall_id, data) => {
    const response = await api.post(`/faelle/${fall_id}/dateien`, data);
    return response.data;
  },

  getDateien: async (fall_id) => {
    const response = await api.get(`/faelle/${fall_id}/dateien`);
    return response.data;
  },

  // Chat
  sendMessage: async (fall_id, nachricht, attachments = []) => {
    const response = await api.post(`/faelle/${fall_id}/chat`, {
      fall_id,
      nachricht,
      attachments
    });
    return response.data;
  },

  getMessages: async (fall_id) => {
    const response = await api.get(`/faelle/${fall_id}/chat`);
    return response.data;
  },

  // Todos
  createTodo: async (fall_id, data) => {
    const response = await api.post(`/faelle/${fall_id}/todos`, {
      fall_id,
      ...data
    });
    return response.data;
  },

  getTodos: async (fall_id) => {
    const response = await api.get(`/faelle/${fall_id}/todos`);
    return response.data;
  },

  updateTodo: async (todo_id, data) => {
    const response = await api.patch(`/faelle/todos/${todo_id}`, data);
    return response.data;
  }
};

// Akten API (NEU)
export const aktenAPI = {
  create: async (fall_id, daten) => {
    const response = await api.post('/akten/create', { fall_id, daten });
    return response.data;
  },

  getByFall: async (fall_id) => {
    const response = await api.get(`/akten/${fall_id}`);
    return response.data;
  },

  update: async (fall_id, daten) => {
    const response = await api.put(`/akten/${fall_id}`, { daten });
    return response.data;
  },

  updateSection: async (fall_id, section_name, section_data) => {
    const response = await api.patch(`/akten/${fall_id}/section/${section_name}`, section_data);
    return response.data;
  },

  delete: async (fall_id) => {
    const response = await api.delete(`/akten/${fall_id}`);
    return response.data;
  }
};

// PDF API (NEU)
export const pdfAPI = {
  generate: async (fall_id) => {
    const response = await api.post(`/pdf/generate/${fall_id}`);
    return response.data;
  },

  download: async (gutachten_id) => {
    const response = await api.get(`/pdf/download/${gutachten_id}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  list: async (fall_id) => {
    const response = await api.get(`/pdf/list/${fall_id}`);
    return response.data;
  },

  delete: async (gutachten_id) => {
    const response = await api.delete(`/pdf/${gutachten_id}`);
    return response.data;
  }
};

// Upload API (NEU)
export const uploadAPI = {
  uploadImage: async (file, fall_id, kategorie = 'schaden', beschreibung = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fall_id', fall_id);
    formData.append('kategorie', kategorie);
    if (beschreibung) formData.append('beschreibung', beschreibung);

    const response = await api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  uploadImagesBulk: async (files, fall_id, kategorie = 'schaden') => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('fall_id', fall_id);
    formData.append('kategorie', kategorie);

    const response = await api.post('/upload/images/bulk', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  listImages: async (fall_id) => {
    const response = await api.get(`/upload/images/${fall_id}`);
    return response.data;
  },

  deleteImage: async (datei_id) => {
    const response = await api.delete(`/upload/image/${datei_id}`);
    return response.data;
  }
};

// Export API (NEU)
export const exportAPI = {
  faelleExcel: async () => {
    const response = await api.get('/export/faelle/excel', {
      responseType: 'blob'
    });
    return response.data;
  },

  faelleCSV: async () => {
    const response = await api.get('/export/faelle/csv', {
      responseType: 'blob'
    });
    return response.data;
  },

  statistikenExcel: async () => {
    const response = await api.get('/export/statistiken/excel', {
      responseType: 'blob'
    });
    return response.data;
  },

  fallDetailExcel: async (fall_id) => {
    const response = await api.get(`/export/fall/${fall_id}/excel`, {
      responseType: 'blob'
    });
    return response.data;
  }
};

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get('/dashboard/stats');
    return response.data;
  },
};

export default api;